import React, { useContext, useEffect } from "react";
import CommonModal from "./CommonModal";
import { Button as NessieButton, ModalTitle, BodyText, Heading, theme } from "@src/components/nessie-web";
import { useFormik } from "formik";
import Input from "@src/components/forms/Input";
import InputLabel from "@src/components/forms/InputLabel";
import Button from "@src/components/Button";
import { validateEmail } from "@src/utils/requests/alternativeAccessMethods";
import { ModalContext, ModalType } from "./ModalController";
import { getSubdomainURL } from "@src/utils/getSubdomainURL";
import { TranslationContext, TranslationType } from "@src/components/translation/TranslationContext";
import { logEvent } from "@src/utils/logClient";
import { AppDataContext } from "../AppDataContext";
import { login } from "@src/utils/axiosLogin";
import { ActivationRedirectionContext } from "@src/contexts/ActivationRedirectionContext";
import styled from "@emotion/styled";
import { useTeacherSignupMobileRedesign } from "@src/hooks/useTeacherSignupMobileRedesign";

const {
  colors: { dt_taro80 },
} = theme;

const Label = styled.label`
  font-weight: 600;
  color: ${dt_taro80};
  margin-bottom: 6px;
  display: block;
`;

function getModalTitle({
  customHeading,
  schoolLeader,
  entityType,
  translate,
}: {
  customHeading?: { title: string };
  schoolLeader?: boolean;
  entityType?: "teacher" | "parent";
  translate: ({ str, fallback }: { str: string; fallback: string }) => TranslationType;
}) {
  if (customHeading?.title) {
    return customHeading.title;
  }
  if (entityType === "parent") {
    return translate({
      str: "components.login.combined_signup.get_started_parent",
      fallback: "Get started as a parent",
    });
  }
  if (schoolLeader) {
    return translate({
      str: "components.login.combined_signup.get_started_school_leader",
      fallback: "Get started as a school leader",
    });
  }
  return translate({
    str: "components.login.combined_signup.get_started_teacher",
    fallback: "Get started as a teacher",
  });
}

function getAccountType({
  schoolLeader,
  entityType = "teacher",
}: {
  schoolLeader?: boolean;
  entityType?: "teacher" | "parent";
}) {
  if (schoolLeader) {
    return "school_leader";
  }
  return entityType;
}

const SignupCombinedModal = ({
  closeModal,
  schoolLeader,
  customHeading,
  dismissCtaAction,
  entityType,
  newLoginFeatures = false,
}: {
  closeModal: () => void;
  schoolLeader?: boolean;
  customHeading?: { title: string; subtitle: string };
  dismissCtaAction?: () => void;
  entityType?: "teacher" | "parent";
  newLoginFeatures?: boolean;
}) => {
  const {
    data: { geolocation },
    services,
  } = useContext(AppDataContext);
  const modalContext = useContext(ModalContext);
  const { translateWithFallback: translate } = useContext(TranslationContext);
  const redirectionContext = useContext(ActivationRedirectionContext);
  const isTeacherSignupMobileRedesign = useTeacherSignupMobileRedesign();

  const defaultLongSessionValue = !(geolocation && geolocation.state === "ny");

  useEffect(() => {
    logEvent({
      eventName: "web.login_or_signup.screen_view",
      metadata: {
        ref: "unknown",
        current_site: "external",
        account_type: getAccountType({ schoolLeader, entityType }),
      },
    });
  }, [schoolLeader]);

  const tryLogin = async ({
    email,
    password,
    longSession,
  }: {
    email: string;
    password: string;
    longSession: boolean;
  }) => {
    try {
      // @ts-expect-error Can be `undefined`
      const response = await login({ login: email, password, longSession, userType: entityType });
      logEvent({
        eventName: "web.autologin.success",
        metadata: {
          current_site: "external",
          account_type: response.data.type,
        },
      });

      // @ts-expect-error Can be `null`
      if (!redirectionContext.getRedirectionStatus()) {
        services.getSession();
        // @ts-expect-error Can be `null`
        redirectionContext.runSuccessAction();
        modalContext.hideModal();
      } else {
        let redirectUrl = process.env.GATSBY_HOME_URL;
        if (response.data.type === "teacher") redirectUrl = process.env.GATSBY_TEACH_URL;
        else if (response.data.type === "student") redirectUrl = process.env.GATSBY_STUDENT_URL;
        else if (response.data.type === "parent") redirectUrl = process.env.GATSBY_HOME_URL;
        window.location.href = redirectUrl ?? "";
      }
      return true;
    } catch (e) {
      logEvent({
        eventName: "web.autologin.failure",
        metadata: {
          current_site: "external",
        },
      });
      return false;
    }
  };

  const emailInput = React.createRef<HTMLInputElement>();

  const form = useFormik({
    initialValues: { email: "", password: "" },
    validate: (values) => {
      const emailRe = /^(?=\S{3,128}$)[^\s\\;,:]+@[^\s\\;,:.]{1}[^\s\\;,:]*\.[^\s\\;,:0-9]+$/;
      if (!values.email) {
        // @ts-expect-error Can be `null`
        emailInput.current.focus();
        return {
          // @ts-expect-error Can be `undefined`
          email: translate({ str: "components.login.combined_signup.required", fallback: "Email address is required" }),
        };
      } else if (!emailRe.test(values.email)) {
        // @ts-expect-error Can be `null`
        emailInput.current.focus();
        return {
          // @ts-expect-error Can be `undefined`
          email: translate({ str: "components.login.combined_signup.invalid_email", fallback: "Invalid email" }),
        };
      }
      return {};
    },
    validateOnChange: false,
    onSubmit: async ({ email, password }, { validateForm, setErrors }) => {
      if (password && newLoginFeatures) {
        const loggedIn = await tryLogin({ email, password, longSession: defaultLongSessionValue });
        if (loggedIn) {
          return;
        }
      }
      const errors = await validateForm({ email });
      if (errors.email) {
        return;
      }
      try {
        const validation = await validateEmail({ email });

        // @ts-expect-error Can be `null`
        if (!validation?.isAvailableForSignUp && validation.entityType) {
          if (validation?.entityType === "teacher") {
            modalContext.showModal(ModalType.TeacherLogin, { emailPrefill: email });
          } else if (validation?.entityType === "parent") {
            modalContext.showModal(ModalType.ParentLogin, { emailPrefill: email });
          } else {
            window.location.href = getSubdomainURL("student");
          }
        } else if (schoolLeader) {
          modalContext.showModal(ModalType.SchoolLeaderSignupForm, { formData: { email } });
        } else if (entityType === "parent") {
          window.location.href = `${getSubdomainURL("parent")}/#/signup?emailAddress=${email}`;
        } else {
          modalContext.showModal(ModalType.TeacherSignupForm, { formData: { email } });
        }
      } catch (e) {
        setErrors({
          // @ts-expect-error Can be `undefined`
          email: translate({
            str: "components.login.combined_signup.invalid_email_validation",
            fallback: "Email validation failed",
          }).toString(),
        });
        return;
      }
    },
  });

  return (
    <CommonModal
      headerText={getModalTitle({
        customHeading,
        schoolLeader,
        entityType,
        // @ts-expect-error Can be `undefined`
        translate,
      })}
      closeModal={closeModal}
      fullPage={isTeacherSignupMobileRedesign}
      useMobileRedesign={isTeacherSignupMobileRedesign}
    >
      <form
        onSubmit={form.handleSubmit}
        style={{
          padding: isTeacherSignupMobileRedesign ? "0" : "0 18px",
          width: "100%",
          maxWidth: "604px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {customHeading && customHeading.subtitle && (
          <BodyText css={{ textAlign: "center" }}>{customHeading.subtitle}</BodyText>
        )}
        <div>
          <div>
            {isTeacherSignupMobileRedesign ? (
              <InputLabel
                rounded
                label={
                  // @ts-expect-error Can be `undefined`
                  translate("components.common.email_address").toString()
                }
                value={form.values.email}
                ref={emailInput}
                onChange={(e) => {
                  form.handleChange(e);
                }}
                onFocus={() => {
                  form.setErrors({ email: "" });
                }}
                autoFocus
                id="email"
                name="email"
                type="email"
                required
                error={!!form.errors.email && form.touched.email}
                aria-invalid={!!form.errors.email && form.touched.email}
                aria-describedby="email-error"
                autoComplete="on"
                newStyles={isTeacherSignupMobileRedesign}
              />
            ) : (
              <>
                <Label htmlFor="email">
                  {
                    // @ts-expect-error Can be `undefined`
                    translate("components.common.email_address").toString()
                  }
                </Label>
                <Input
                  rounded
                  value={form.values.email}
                  ref={emailInput}
                  onChange={(e) => {
                    form.handleChange(e);
                  }}
                  onFocus={() => {
                    form.setErrors({ email: "" });
                  }}
                  autoFocus
                  id="email"
                  name="email"
                  type="email"
                  required
                  error={!!form.errors.email && form.touched.email}
                  aria-label={
                    // @ts-expect-error Can be `undefined`
                    translate({ str: "components.common.email_address", fallback: "Email address" }).toString()
                  }
                  aria-invalid={!!form.errors.email && form.touched.email}
                  aria-describedby="email-error"
                  autoComplete="on"
                />
              </>
            )}
          </div>
          <div style={{ opacity: 0, height: 0 }} aria-hidden>
            <Input
              tabIndex={-1}
              aria-label={
                // @ts-expect-error Can be `undefined`
                translate("components.common.password").toString()
              }
              placeholder={
                // @ts-expect-error Can be `undefined`
                translate("components.common.password").toString()
              }
              autoComplete="password"
              id="password"
              type="password"
              name="password"
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onChange={newLoginFeatures ? form.handleChange : () => {}}
              value={form.values.password}
            />
          </div>
          <div
            id="email-error"
            style={{
              // set for transition to work when expanding/shrinking
              maxHeight: form.errors.email && form.touched.email ? "14px" : "0px",
              minHeight: form.errors.email ? "14px" : "0px",
              transition: "0.2s",
              fontSize: "14px",
              marginTop: "6px",
              marginLeft: "8px",
            }}
          >
            {form.errors.email}
          </div>
        </div>
        <Button type="submit" style={{ width: "100%", minHeight: "50px" }} size="l" disabled={form.isSubmitting}>
          {
            // @ts-expect-error Can be `undefined`
            translate({ str: "components.login.combined_signup.continue", fallback: "Continue" })
          }
        </Button>
        {dismissCtaAction && (
          <NessieButton
            style={{
              marginTop: 12,
              width: "100%",
            }}
            size="s"
            kind="tertiary"
            onClick={() => {
              dismissCtaAction();
              closeModal();
            }}
          >
            {
              // @ts-expect-error Can be `undefined`
              translate({ str: "components.login.combined_signup.maybe_later", fallback: "Proceed without signing up" })
            }
          </NessieButton>
        )}
      </form>
    </CommonModal>
  );
};

export default SignupCombinedModal;
